import { useEffect, useRef, useState } from "react"

/**
 * Types through `questions` one at a time, then stops after the last one and
 * leaves the placeholder blank. Stops immediately when `enabled` becomes false
 * (e.g. the user focuses the input).
 *
 * Usage:
 *   const placeholder = useTypewriterPlaceholder(EXAMPLE_QUESTIONS, { enabled: text === "" && !dismissed })
 */
const useTypewriterPlaceholder = (
    questions,
    { enabled, charDelayMs = 60, holdDurationMs = 2500, gapBeforeNextMs = 400 } = {}
) => {
    const [placeholderText, setPlaceholderText] = useState("")
    const questionIndexRef = useRef(0)
    const charIndexRef = useRef(0)
    const timeoutRef = useRef(null)

    useEffect(() => {
        const clearTimers = () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }

        if (!enabled || !questions?.length) {
            clearTimers()
            setPlaceholderText("")
            questionIndexRef.current = 0
            charIndexRef.current = 0
            return clearTimers
        }

        const typeStep = () => {
            const question = questions[questionIndexRef.current]
            if (!question) {
                setPlaceholderText("")
                return
            }
            charIndexRef.current += 1
            setPlaceholderText(question.slice(0, charIndexRef.current))

            if (charIndexRef.current < question.length) {
                timeoutRef.current = setTimeout(typeStep, charDelayMs)
            } else if (questionIndexRef.current < questions.length - 1) {
                timeoutRef.current = setTimeout(clearStep, holdDurationMs)
            } else {
                timeoutRef.current = setTimeout(() => {
                    setPlaceholderText("")
                }, holdDurationMs)
            }
        }

        const clearStep = () => {
            setPlaceholderText("")
            questionIndexRef.current += 1
            charIndexRef.current = 0
            if (questionIndexRef.current < questions.length) {
                timeoutRef.current = setTimeout(typeStep, gapBeforeNextMs)
            }
        }

        questionIndexRef.current = 0
        charIndexRef.current = 0
        typeStep()

        return clearTimers
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, questions])

    return placeholderText
}

export { useTypewriterPlaceholder }
export default useTypewriterPlaceholder
